import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { getCurrentUser } from '../../lib/auth';
// ── Reasons per entity type — matches artifact ────────────────────────────────
const REASONS = {
  post: [
    'Unmarked spoiler',
    'Spam',
    'Harassment',
    'Hate speech',
    'Misinformation',
    'Other',
  ],
  book: [
    'Misleading tags',
    'Wrong metadata',
    'Inappropriate content',
    'Duplicate',
    'Other',
  ],
  // A link is reported for where it goes, not for what it says, so none of
  // the other lists fit. Note REASONS[entityType] falls back to REASONS.post
  // below — without this key a reported link would offer "Unmarked spoiler"
  // and "Hate speech", which is not wrong so much as useless to the person
  // trying to say a link is a scam.
  custom_link: [
    'Spam or scam',
    'Malware or phishing',
    'Inappropriate content',
    'Not what it claims to be',
    'Other',
  ],
  user: [
    'Harassment',
    'Spam',
    'Impersonation',
    'Inappropriate profile content',
    'Other',
  ],
  pipeline: [
    'Wrong book pairing',
    'Inaccurate tags',
    'Duplicate',
    'Inappropriate content',
    'Other',
  ],
};

const ENTITY_LABEL = {
  post: 'post',
  book: 'book',
  custom_link: 'link',
  user: 'user',
  pipeline: 'pipeline',
};

// ══════════════════════════════════════════════════════════════════════════════
// KEBAB MENU — three-dot button + dropdown, used on posts / books / user profiles
// Usage:
//   <KebabMenu>
//     <KebabItem onClick={...}>🚩 Report</KebabItem>
//   </KebabMenu>
// ══════════════════════════════════════════════════════════════════════════════
export function KebabMenu({ children, dark = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (!ref.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={e => { e.stopPropagation(); setOpen(p => !p); }}
        style={{
          width: 32, height: 32, borderRadius: 'var(--radius-circle)', border: 'none',
          background: open
            ? (dark ? 'rgba(255,255,255,0.15)' : 'var(--brand-cream)')
            : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', transition: 'background 0.15s', flexShrink: 0,
          color: dark ? 'var(--neutral-300)' : 'var(--neutral-500)',
        }}
        onMouseEnter={e => e.currentTarget.style.background = dark ? 'rgba(255,255,255,0.12)' : '#f5f0e8'}
        onMouseLeave={e => e.currentTarget.style.background = open ? (dark ? 'rgba(255,255,255,0.15)' : '#f5f0e8') : 'transparent'}
      >
        {/* Three dots */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {[0,1,2].map(i => (
            <span key={i} style={{ width: 3.5, height: 3.5, borderRadius: 'var(--radius-circle)', background: 'currentColor', display: 'block' }} />
          ))}
        </div>
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'absolute', top: 'calc(100% + 5px)', right: 0,
            background: 'var(--brand-white)', border: '1px solid var(--brand-sand)',
            borderRadius: 'var(--radius-md)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            minWidth: 140, zIndex: 200, overflow: 'hidden',
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export function KebabItem({ children, onClick, danger }) {
  return (
    <button
      onClick={e => { e.stopPropagation(); onClick?.(); }}
      style={{
        display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
        padding: '10px 14px', width: '100%', textAlign: 'left',
        border: 'none', background: 'transparent', cursor: 'pointer',
        fontSize: 'var(--font-size-base)', fontFamily: 'var(--font-body)',
        color: danger ? 'var(--feedback-error)' : 'var(--neutral-700)',
      }}
      onMouseEnter={e => e.currentTarget.style.background = danger ? '#fff8f8' : '#faf8f5'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      {children}
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// REPORT MODAL — matches artifact exactly
// Props:
//   entityType: 'post' | 'book' | 'user'
//   entityId:   string or number
//   reportedUserId: uuid (optional — the user who owns the content)
//   onClose: fn
// ══════════════════════════════════════════════════════════════════════════════
// contextNote: something the moderator needs that the entity id does not carry
// on its own. A custom link passes its URL, because entity_id names WHICH link
// but not where it goes, and where it goes is the entire report.
export default function ReportModal({ entityType, entityId, reportedUserId, contextNote, onClose }) {
  const [selected, setSelected] = useState('');
  const [notes, setNotes]       = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]         = useState(false);

  const reasons  = REASONS[entityType] || REASONS.post;
  const label    = ENTITY_LABEL[entityType] || 'content';
  const isOther  = selected === 'Other';
  const canSubmit = selected && (!isOther || notes.trim().length > 0) && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const user = await getCurrentUser();
      if (!user) { alert('Please log in to report content.'); setSubmitting(false); return; }

      // `error` is destructured, and this is not cosmetic. Supabase RETURNS a
      // constraint violation, it does not throw one, so the try/catch around
      // this block never saw it: a report rejected by reports_entity_type_check
      // set done = true and thanked the reporter for something that was never
      // stored. Reporting is the one flow where a false confirmation is worse
      // than an error message, because nobody reports the same thing twice.
      const { error } = await supabase.from('reports').insert({
        entity_type:      entityType,
        entity_id:        String(entityId),
        reported_user_id: reportedUserId || null,
        reporter_id:      user.id,
        reason:           selected,
        // contextNote goes in front of the reporter's own words rather than
        // replacing them -- notes is their text, and a moderator needs both.
        notes:            [contextNote, notes.trim()].filter(Boolean).join('\n') || null,
        status:           'open',
      });
      if (error) throw new Error(error.message);

      setDone(true);
    } catch (err) {
      alert('Failed to submit: ' + err.message);
    }
    setSubmitting(false);
  }

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(28,25,23,0.6)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'var(--space-xl)',
        animation: 'reportFadeIn 0.2s ease',
      }}
    >
      <style>{`
        @keyframes reportFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes reportSlideUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
      `}</style>

      <div style={{
        background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', width: '100%', maxWidth: 420,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        overflow: 'hidden', fontFamily: 'var(--font-body)',
        animation: 'reportSlideUp 0.22s ease',
      }}>

        {done ? (
          /* ── Success state — matches artifact ── */
          <div style={{ padding: '40px 26px', textAlign: 'center' }}>
            <div style={{
              width: 52, height: 52, borderRadius: 'var(--radius-circle)',
              background: '#d1fae5', color: '#16a34a',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 'var(--font-size-h4)', margin: '0 auto 14px',
            }}>
              ✓
            </div>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 19, fontWeight: 700, color: 'var(--neutral-900)', marginBottom: 'var(--space-6)', letterSpacing: -0.3 }}>
              Report submitted
            </p>
            <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', lineHeight: 1.5, marginBottom: 'var(--space-xl)' }}>
              Thanks, we'll take it from here.
            </p>
            <button
              onClick={onClose}
              style={{ padding: '9px 24px', background: 'var(--brand-rust)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ padding: '22px 26px 16px', borderBottom: '1px solid var(--brand-cream)', display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 19, fontWeight: 700, color: 'var(--neutral-900)', flex: 1, letterSpacing: -0.3, margin: 'var(--space-none)' }}>
                Report {label}
              </p>
              <button
                onClick={onClose}
                style={{ width: 28, height: 28, borderRadius: 'var(--radius-circle)', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--neutral-400)', fontSize: 'var(--font-size-xl)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f5f0e8'; e.currentTarget.style.color = '#292524'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#a8a29e'; }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '20px 26px' }}>
              <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-600)', marginBottom: 'var(--space-md)' }}>
                What's the issue?
              </p>

              {/* Reason list — radio style matching artifact */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', marginBottom: 'var(--space-lg)' }}>
                {reasons.map(r => {
                  const isSelected = selected === r;
                  return (
                    <div
                      key={r}
                      onClick={() => setSelected(r)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 11,
                        padding: '11px 13px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                        border: `1px solid ${isSelected ? '#B85C38' : '#e8e4dc'}`,
                        background: isSelected ? '#fef8f5' : 'var(--brand-white)',
                        transition: 'all 0.12s',
                      }}
                      onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.borderColor = '#a8a29e'; e.currentTarget.style.background = '#faf8f5'; }}}
                      onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.borderColor = '#e8e4dc'; e.currentTarget.style.background = '#fff'; }}}
                    >
                      {/* Custom radio */}
                      <div style={{
                        width: 15, height: 15, borderRadius: 'var(--radius-circle)', flexShrink: 0,
                        border: `2px solid ${isSelected ? '#B85C38' : '#d6d3d1'}`,
                        position: 'relative', transition: 'border-color 0.12s',
                      }}>
                        {isSelected && (
                          <div style={{
                            position: 'absolute', top: 2, left: 2,
                            width: 7, height: 7, borderRadius: 'var(--radius-circle)',
                            background: 'var(--brand-rust)',
                          }} />
                        )}
                      </div>
                      <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--neutral-800)' }}>{r}</span>
                    </div>
                  );
                })}
              </div>

              {/* Notes textarea */}
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder={isOther ? 'Please explain (required)' : 'Add details (optional)'}
                maxLength={500}
                rows={2}
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 'var(--font-size-base)',
                  border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', outline: 'none',
                  resize: 'vertical', minHeight: 64,
                  fontFamily: 'var(--font-body)', color: 'var(--neutral-800)',
                  background: 'var(--brand-white)', boxSizing: 'border-box',
                  transition: 'border-color 0.15s',
                }}
                onFocus={e => e.target.style.borderColor = '#a8a29e'}
                onBlur={e => e.target.style.borderColor = '#e8e4dc'}
              />
            </div>

            {/* Footer */}
            <div style={{
              padding: '14px 26px', background: 'var(--brand-paper)',
              borderTop: '1px solid var(--brand-cream)',
              display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-sm)',
            }}>
              <button
                onClick={onClose}
                style={{ padding: '9px 18px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', background: 'transparent', color: 'var(--neutral-600)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#d6d3d1'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#e8e4dc'; }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                style={{
                  padding: '9px 18px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-rust)',
                  background: canSubmit ? 'var(--brand-rust)' : 'var(--brand-sand)',
                  color: canSubmit ? 'var(--brand-white)' : 'var(--neutral-400)',
                  fontSize: 'var(--font-size-base)', fontWeight: 600,
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                  fontFamily: 'var(--font-body)', transition: 'all 0.15s',
                }}
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}